import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const MonthlyAnnualIncomeStatement = ({ annualSummary, monthlySchedule, totalMetrics }) => {
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const parseDate = (dateString) => {
    const date = new Date(dateString);
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      formattedDate: format(parseISO(dateString), 'MMM yyyy')
    };
  };

  const normalizedAnnual = useMemo(() => {
    if (!annualSummary || !Array.isArray(annualSummary)) return [];
    return annualSummary.map(item => {
      const roa = item.return_on_assets
        ?? item.return_on_average_assets
        ?? (item.total_assets ? item.net_income / item.total_assets : null);
      const roe = item.return_on_equity
        ?? item.return_on_average_equity
        ?? (item.total_equity ? item.net_income / item.total_equity : null);
      const netInterestIncome =
        (item.interest_income_loans || 0) +
        (item.interest_income_lines || 0) +
        (item.origination_fees || 0) +
        (item.unused_commitment_fees || 0) -
        (item.interest_expense || 0) -
        (item.deferred_cost_amortization || 0);
      const earningAssets = (item.loan_balance || 0) + (item.line_balance || 0);
      const nim = earningAssets > 0 ? netInterestIncome / earningAssets : item.net_interest_margin ?? null;
      const totalRevenue =
        (item.interest_income_loans || 0) +
        (item.interest_income_lines || 0) +
        (item.origination_fees || 0) +
        (item.unused_commitment_fees || 0);
      return {
        ...item,
        return_on_assets: roa,
        return_on_equity: roe,
        net_interest_margin: nim,
        total_revenue: item.total_revenue ?? totalRevenue,
      };
    });
  }, [annualSummary]);

  const years = useMemo(() => {
    if (normalizedAnnual.length === 0) {
      return [];
    }
    return [...new Set(normalizedAnnual.map(item => parseDate(item.index).year))];
  }, [normalizedAnnual]);

  const formatCurrency = (value) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatPercentage = (value) => {
    if (value == null) return '-';
    return (value * 100).toFixed(2) + '%';
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const toggleRow = (rowKey) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const renderTableHeader = () => {
    return (
      <tr className="ace-table-header custom-text-size-header">
        <th className="px-4 py-2 text-left" style={{ width: "calc(12rem * 1.3)" }}>Metric</th>
        {years.map(year => (
          <React.Fragment key={year}>
            {expandedYears[year] && Array.from({length: 12}, (_, i) => (
              <th key={`${year}-${i+1}`} className="px-2 py-2 text-right">
                {format(new Date(year, i, 1), 'MMM')} {year}
              </th>
            ))}
            <th 
              className="px-2 py-2 text-right cursor-pointer" 
              onClick={() => toggleYear(year)}
            >
              {year} {expandedYears[year] ? <ChevronDown className="inline" /> : <ChevronRight className="inline" />}
            </th>
          </React.Fragment>
        ))}
        <th className="px-2 py-2 text-right">Total</th>
      </tr>
    );
  };

  const renderDataRow = (metric, label, isSubItem = false, isTotalRow = false, formatFunc = formatCurrency, calculateTotal = null, parentKey = null, totalAccessor = null) => {
    const rowKey = parentKey || metric;
    const isExpanded = expandedRows[rowKey];
    const hasSubItems = !isSubItem && ['total_interest_income', 'non_interest_income', 'non_interest_expense'].includes(metric);

    if (isSubItem && !isExpanded) {
      return null;
    }

    return (
      <React.Fragment key={metric}>
        <tr className={`custom-text-size-body ${isTotalRow ? 'font-bold' : ''} hover:bg-muted/50`}>
          <td 
            className={`px-4 py-2 ${isSubItem ? 'pl-8' : ''}`} 
            style={{ width: "calc(12rem * 1.3)", cursor: hasSubItems ? 'pointer' : 'default' }}
            onClick={() => hasSubItems && toggleRow(rowKey)}
          >
            <span className="flex items-center justify-between">
              {label}
              {hasSubItems && (
                <span className="ml-2">
                  {isExpanded ? (
                    <ChevronDown className="inline" style={{ width: '0.84em', height: '0.84em' }} />
                  ) : (
                    <ChevronRight className="inline" style={{ width: '0.84em', height: '0.84em' }} />
                  )}
                </span>
              )}
            </span>
          </td>
          {years.map(year => (
            <React.Fragment key={year}>
              {expandedYears[year] && Array.from({length: 12}, (_, i) => {
                const monthData = monthlySchedule?.find(item => {
                  const itemDate = parseDate(item.index);
                  return itemDate.year === year && itemDate.month === i;
                });
                const value = monthData ? (calculateTotal ? calculateTotal(monthData) : monthData[metric]) : null;
                const isNegative = typeof value === 'number' && value < 0;
                return (
                  <td key={`${year}-${i+1}`} className={`px-2 py-2 text-right ${isNegative ? 'text-destructive' : ''}`}>
                    {value !== null ? formatFunc(value) : '-'}
                  </td>
                );
              })}
              {(() => {
              const annualItem = normalizedAnnual?.find(item => parseDate(item.index).year === year);
                const annualValRaw = annualItem ? (calculateTotal ? calculateTotal(annualItem) : annualItem[metric]) : null;
                const annualNeg = typeof annualValRaw === 'number' && annualValRaw < 0;
                return (
                  <td className={`px-2 py-2 text-right font-bold ${annualNeg ? 'text-destructive' : ''}`}>
                    {formatFunc(annualValRaw)}
                  </td>
                );
              })()}
            </React.Fragment>
          ))}
          {(() => {
            const totalVal = typeof totalAccessor === 'function'
              ? totalAccessor(totalMetrics)
              : (totalMetrics?.[`total_${metric}`] || totalMetrics?.[`average_${metric}`] || totalMetrics?.[metric] || null);
            const totalNeg = typeof totalVal === 'number' && totalVal < 0;
            return (
              <td className={`px-2 py-2 text-right font-bold ${totalNeg ? 'text-destructive' : ''}`}>
                {formatFunc(totalVal)}
              </td>
            );
          })()}
        </tr>
        {hasSubItems && (
          <>
            {metric === 'total_interest_income' && (
              <>
                {renderDataRow('interest_income_loans', 'Interest Income (Loans)', true, false, formatFunc, null, rowKey)}
                {renderDataRow('interest_income_lines', 'Interest Income (Lines)', true, false, formatFunc, null, rowKey)}
              </>
            )}
            {metric === 'non_interest_income' && (
              <>
                {renderDataRow('origination_fees', 'Origination Fees', true, false, formatFunc, null, rowKey)}
                {renderDataRow('unused_commitment_fees', 'Unused Commitment Fees', true, false, formatFunc, null, rowKey)}
              </>
            )}
            {metric === 'non_interest_expense' && (
              <>
                {renderDataRow('salary_expense', 'Salary Expense', true, false, formatFunc, null, rowKey)}
                {renderDataRow('benefits_expense', 'Benefits Expense', true, false, formatFunc, null, rowKey)}
                {renderDataRow('incentive_compensation_expense', 'Incentive Compensation Expense', true, false, formatFunc, null, rowKey)}
                {renderDataRow('discretionary_expense', 'Discretionary Expense', true, false, formatFunc, null, rowKey)}
              </>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  if (!normalizedAnnual || normalizedAnnual.length === 0 || !monthlySchedule || !totalMetrics || !Array.isArray(monthlySchedule) || monthlySchedule.length === 0) {
    return (
      <Card className="card-wrapper mt-6">
        <CardHeader>
          <CardTitle>Income Statement</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-foreground">No data available for the Income Statement.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-wrapper mt-6">
      <CardHeader>
        <CardTitle className="custom-text-size">Income Statement</CardTitle>
      </CardHeader>
      <CardContent>
        <style jsx>{`
          .custom-text-size {
            font-size: 110%;
          }
          .custom-text-size-body {
            font-size: calc(0.75rem * 1.1); /* 0.75rem is the base size for text-xs */
          }
          .custom-text-size-header {
            font-size: 95%; /* Changed from 85% to 95% */
          }
        `}</style>
        <div className="ace-scroll-shell overflow-x-auto">
          <table className="min-w-full">
            <thead className="custom-text-size-header">
              {renderTableHeader()}
            </thead>
            <tbody className="custom-text-size-body">
              {renderDataRow(
                'total_interest_income',
                'Total Interest Income',
                false,
                true,
                formatCurrency,
                (data) => (data.interest_income_loans || 0) + (data.interest_income_lines || 0),
                null,
                (totals) => totals?.total_interest_income_gross ?? null
              )}
              {renderDataRow('interest_expense', 'Interest Expense')}
              {renderDataRow('non_interest_income', 'Non-Interest Income', false, true, formatCurrency, 
                (data) => (data.origination_fees || 0) + (data.unused_commitment_fees || 0))}
              {renderDataRow(
                'total_revenue',
                'Total Revenue',
                false,
                true,
                formatCurrency,
                (data) =>
                  (data.interest_income_loans || 0) +
                  (data.interest_income_lines || 0) +
                  (data.origination_fees || 0) +
                  (data.unused_commitment_fees || 0),
                null,
                (totals) => totals?.total_revenue ?? null
              )}
              {renderDataRow('non_interest_expense', 'Non-Interest Expense', false, true, formatCurrency, 
                (data) => (data.salary_expense || 0) + (data.benefits_expense || 0) + 
                          (data.incentive_compensation_expense || 0) + (data.discretionary_expense || 0))}
              {renderDataRow('provision_expense', 'Provision Expense')}
              {renderDataRow('net_income', 'Net Income', false, true)}
              {renderDataRow('net_income_cagr', 'Net Income CAGR', false, false, formatPercentage, null, null, (totals) => totals?.net_income_cagr ?? null)}
              {renderDataRow('efficiency_ratio', 'Efficiency Ratio', false, false, formatPercentage)}
              {renderDataRow('return_on_assets', 'Return on Assets', false, false, formatPercentage, null, null, (totals) => totals?.return_on_average_assets ?? totals?.return_on_assets ?? null)}
              {renderDataRow('return_on_equity', 'Return on Equity', false, false, formatPercentage, null, null, (totals) => totals?.return_on_average_equity ?? totals?.return_on_equity ?? null)}
              {renderDataRow('net_interest_margin', 'Net Interest Margin', false, false, formatPercentage, null, null, (totals) => totals?.net_interest_margin ?? null)}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyAnnualIncomeStatement;